import { useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "sonner";

import type { WorkspaceDetail, WorkspaceNode } from "../../../types/workspace";
import { findWorkspaceNodeForDeepLink } from "./workspaceGraphUtils";
import {
  parseWorkspaceNodeSearchParam,
  withWorkspaceNodeSearch,
  type WorkspaceNodeDeepLink,
} from "./workspaceRoutes";

function deepLinkKey(
  workspaceId: string,
  link: WorkspaceNodeDeepLink | null
): string | null {
  if (!link) return null;
  return `${workspaceId}:${link.type}:${link.entityId}`;
}

function drawerStackKey(nodes: WorkspaceNode[]): string {
  return nodes.map((node) => node.id).join(",");
}

/**
 * Open a drawer from `?node=type/entityId`, and keep the query in sync with
 * the focused (newest) drawer when the stack changes.
 */
export function useWorkspaceNodeDeepLink(args: {
  workspaceId: string;
  detail: WorkspaceDetail | null;
  loading: boolean;
  drawerNodes: WorkspaceNode[];
  openDrawer: (node: WorkspaceNode) => void;
  setSelectedNodeId: (nodeId: string | null) => void;
}): void {
  const { workspaceId, detail, loading, drawerNodes, openDrawer, setSelectedNodeId } =
    args;
  const location = useLocation();
  const history = useHistory();
  const appliedKeyRef = useRef<string | null>(null);
  const prevDrawerKeyRef = useRef<string | null>(null);

  const deepLink = useMemo(
    () => parseWorkspaceNodeSearchParam(location.search),
    [location.search]
  );

  // Apply incoming deep link once detail is available.
  useEffect(() => {
    const key = deepLinkKey(workspaceId, deepLink);
    if (!key || !deepLink) return;
    if (appliedKeyRef.current === key) return;
    if (loading || !detail) return;

    const node = findWorkspaceNodeForDeepLink(detail, deepLink.type, deepLink.entityId);
    if (node) {
      appliedKeyRef.current = key;
      setSelectedNodeId(node.id);
      openDrawer(node);
      return;
    }

    appliedKeyRef.current = key;
    toast.error("That item is not on this workspace canvas");
    const nextSearch = withWorkspaceNodeSearch(location.search, null);
    if (nextSearch !== (location.search || "")) {
      history.replace({ pathname: location.pathname, search: nextSearch });
    }
  }, [
    deepLink,
    detail,
    history,
    loading,
    location.pathname,
    location.search,
    openDrawer,
    setSelectedNodeId,
    workspaceId,
  ]);

  // Mirror drawer stack changes into the URL (skip the initial empty mount).
  useEffect(() => {
    const ids = drawerStackKey(drawerNodes);
    const prev = prevDrawerKeyRef.current;
    prevDrawerKeyRef.current = ids;

    if (prev === null) {
      // First paint: wait for deep-link apply (or a user open) before writing.
      if (!ids) return;
    } else if (prev === ids) {
      return;
    }

    const focus = drawerNodes[drawerNodes.length - 1];
    const nextNode: WorkspaceNodeDeepLink | null = focus
      ? { type: focus.type, entityId: focus.entityId }
      : null;
    const nextSearch = withWorkspaceNodeSearch(location.search, nextNode);
    if (nextSearch === (location.search || "")) return;

    if (nextNode) {
      appliedKeyRef.current = deepLinkKey(workspaceId, nextNode);
    } else {
      appliedKeyRef.current = null;
    }

    history.replace({ pathname: location.pathname, search: nextSearch });
  }, [drawerNodes, history, location.pathname, location.search, workspaceId]);
}
